import {
  ChangeEvent,
  ChangeEventHandler,
  MouseEventHandler,
  Dispatch,
  SetStateAction
} from 'react';
import {
  chakra,
  Box,
  HStack,
  VStack,
  Container,
  Text,
  Input,
} from '@chakra-ui/react';
import StepSectionHeading, {STICKING_OUT, STICKING_OUT_SM} from '../components/StepSectionHeading';
import AddButton from '../components/AddButton';
import DeleteButton from '../components/DeleteButton';
import StepExamples from '../components/StepExamples';
import {createEstate} from '../models';

type Props = {
  id: string,
  estates: Estate[],
  setEstates: Dispatch<SetStateAction<Estate[]>>
};

export default function Estates({id, estates, setEstates}: Props) {
  const addEstateInput = () => setEstates(prev => [...prev, createEstate()]);
  const deleteEstate = (index: number) => setEstates(prev => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  const onChange = (index: number, event: ChangeEvent<HTMLInputElement>) =>
    setEstates(prev => [
      ...prev.slice(0, index),
      {...prev[index], name: event.target.value},
      ...prev.slice(index + 1)
    ]);

  return (
    <chakra.section
      id={id}
      pt={[STICKING_OUT_SM, STICKING_OUT_SM, STICKING_OUT]}
    >
      <StepSectionHeading number={1}>
        <Box maxW={['contentSm', 'contentSm', 'content']} mx="auto">
          “分人を振り返ってみたい時期”を
          <chakra.br display={['none', 'none', 'inline']} />
          考えてみましょう
        </Box>
      </StepSectionHeading>
      <Container maxW={['contentSm', 'contentSm', 'content']} px={0}>
        <Box mt={[8, 8, `${67/16}rem`]} lineHeight={[1.5, 1.5, 2]}>
          <Text>大きく物事が動いたライフイベントやターニングポイントの前後など、「分人主義」を通して振り返ってみたい時期を書き出してみましょう。</Text>
          <Text>現在の「分人」を書き出してみる他、振り返りたい過去の時期をいくつか選んで、分人の構成比率を見比べることも出来ます。</Text>
          <Text>これまで過ごしてきた人生の中で、じっくり「分人」を振り返ってみたい時期はありますか？</Text>
        </Box>
        <StepExamples
          examples={[
            '10代、20代、30代…と過ごしてきた年代と現在を比較してみる',
            '親になるまで、親になってからの自分',
            '現在の自分と、これから目指したい理想の自分を比較してみる'
          ]}
          mt={[`${84/16}rem`, `${84/16}rem`, `${124/16}rem`]}
        />
        <VStack
          spacing="6"
          mt={[9, 9, 7]}
          pt={[`${30/16}rem`, `${30/16}rem`, `${68/16}rem`]}
          pb={[10, 10, 16]}
          pl={[`${10/16}rem`, `${10/16}rem`, `${122/16}rem`]}
          pr={[5, 5, 32]}
          borderRadius="3xl"
          bgColor="gray.100"
        >
          <VStack spacing="7.5" w="full" align="flex-end">
            {estates.map(({id, name}, index) =>
              <EstateInput
                key={id}
                name={name}
                deletable={index !== 0}
                deleteEstate={() => deleteEstate(index)}
                onChange={event => onChange(index, event)}
              />)} 
          </VStack>
          <AddButton label="入力欄を追加" onClick={addEstateInput} />
        </VStack>
      </Container>
    </chakra.section>
  );
}

type EstateInputProps = {
  name: string,
  deletable: boolean,
  deleteEstate?: MouseEventHandler<HTMLButtonElement>,
  onChange: ChangeEventHandler<HTMLInputElement>
};

function EstateInput({
  name,
  deletable,
  deleteEstate,
  onChange
}: EstateInputProps) {
  return (
    <HStack spacing={[2, 2, 4]} fontSize="lg" flexWrap={['wrap', 'wrap', 'nowrap']} justify={['flex-end', 'flex-end', 'space-between']} w="full" lineHeight="calc(100% + 5px)">
      {deletable ? <DeleteButton w="27px" h="27px" minW="auto" onClick={deleteEstate} flexShrink="0" /> : <Box w="28px" h="28px" role="presentation" flexShrink="0"></Box>}
      <Input value={name} onChange={onChange} placeholder="現在" w={[`calc(100% - ${28 + 2 * 4}px)`, `calc(100% - ${28 + 2 * 4}px)`, 'auto']} h="auto" flexGrow={[0, 0, 1]} py="1rem" px="1.125rem" bgColor="white" />
      <chakra.span flexShrink="0">のとき</chakra.span>
    </HStack>
  );
}
